// src/data/products/productsRegistry.ts
//
// ─── HOW TO ADD A NEW PRODUCT ─────────────────────────────────────────────
//
// 1. Create the JSON file: src/data/products/your-slug.json
//    (Follow the same shape as any existing product JSON)
//
// 2. Import it here:
//    import yourProduct from "./your-slug.json";
//
// 3. Add it to PRODUCTS_REGISTRY (featured product first, rest in any order):
//    {
//      ...productMeta(yourProduct),
//      featured: false,
//    },
//
// Products.tsx, SingleProduct.tsx, and Home.tsx demos all update automatically.
// ─────────────────────────────────────────────────────────────────────────────

import launchKitNaija from "./launch-kit-naija.json";
import myCreatorTool from "./mycreatortool.json";
import knowledgeMillionaire from "./knowledge-millionaire.json";
import futureSmartLab from "./future-smart-lab.json";
//import onlineCourse from "./online-course-platform.json";
//import mechtrack from "./mechtrack.json";
//import hrflowNigeria from "./hrflow-nigeria.json";
//import azatme from "./azatme.json";

// ─── Shared types ─────────────────────────────────────────────────────────

export type ProductStatus = "Live" | "Beta" | "In Development";

export interface KeyFeature {
  icon: string;
  title: string;
  body: string;
}

export interface TechLayer {
  layer: string;
  tech: string;
}

export interface Screenshot {
  label: string;
  image: string;
}

export interface Metric {
  value: string;
  label: string;
}

/** Full product data — used by SingleProduct detail page */
export interface ProductData {
  slug: string;
  tag: string;
  status: ProductStatus;
  title: string;
  tagline: string;
  heroImage: string;
  overview: string[];
  keyFeatures: KeyFeature[];
  techStack: TechLayer[];
  screenshots: Screenshot[];
  metrics?: Metric[];
  demoUrl?: string;
}

/** Lightweight product card data — used by Products listing & Home demos */
export interface ProductCard {
  slug: string;
  tag: string;
  status: ProductStatus;
  title: string;
  excerpt: string;
  techStack: string[];
  image: string;
  href: string;
  demoHref: string;
  featured: boolean;
}

// ─── Helper: lift card-level metadata from a JSON import ──────────────────

function toProductStatus(status: string): ProductStatus {
  if (status === "Live" || status === "Beta" || status === "In Development") {
    return status;
  }
  return "In Development";
}

function productMeta(json: {
  slug: string;
  tag: string;
  status: string;
  title: string;
  tagline: string;
  heroImage: string;
  overview: string[];
  techStack: { layer: string; tech: string }[];
  demoUrl?: string;
}): Omit<ProductCard, "featured"> {
  return {
    slug: json.slug,
    tag: json.tag,
    status: toProductStatus(json.status),
    title: json.title,
    excerpt: json.overview[0],
    techStack: json.techStack.map((t) => t.tech),
    image: json.heroImage,
    href: `/products/${json.slug}`,
    demoHref: json.demoUrl ?? `/demos/${json.slug}`,
  };
}

// ─── Registry ─────────────────────────────────────────────────────────────
// Featured product must come first. Only one entry should have featured: true.

export const PRODUCTS_REGISTRY: ProductCard[] = [
  {
    ...productMeta(launchKitNaija),
    // Override excerpt with a tighter card-length pitch than overview[0]
    excerpt:
      "Everything you need to sell online in Nigeria — website, sales funnels, video courses, email & SMS marketing, AI copywriter and Paystack payments in one platform. Priced in naira from ₦4,999/month, 0% commission on sales.",
    featured: true,
  },
  {
    ...productMeta(myCreatorTool),
    excerpt:
      "AI business planning & daily execution for entrepreneurs. Describe your business once — the AI plans your year, schedules your week, writes your pitches and emails, and even writes your book and online course.",
    featured: false,
  },
  {
    ...productMeta(knowledgeMillionaire),
    excerpt:
      "Education venture for knowledge entrepreneurs — a free-workshop funnel, programmes storefront and Paystack checkout, with coaching enrolment that auto-provisions LaunchKit Naija and MyCreatorTool accounts.",
    featured: false,
  },
  {
    ...productMeta(futureSmartLab),
    excerpt:
      "Premium tech & entrepreneurship programme for ages 6–18 in Abuja. We built its animated marketing site, custom brand design system, enrolment flows and full SEO — from Scratch classes to founder capstones.",
    featured: false,
  },
];

// ─── Full product data map — used by SingleProduct ────────────────────────

export const PRODUCT_DATA_MAP: Record<string, ProductData> = {
  "launch-kit-naija": launchKitNaija as unknown as ProductData,
  mycreatortool: myCreatorTool as unknown as ProductData,
  "knowledge-millionaire": knowledgeMillionaire as unknown as ProductData,
  "future-smart-lab": futureSmartLab as unknown as ProductData,
};

// ─── Derived views ────────────────────────────────────────────────────────

/** The single featured product — shown as the hero card on Products page */
export const FEATURED_PRODUCT = PRODUCTS_REGISTRY.find((p) => p.featured)!;

/** All non-featured products — shown in the grid */
export const GRID_PRODUCTS = PRODUCTS_REGISTRY.filter((p) => !p.featured);
